import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import EditableImage from "../../landingEditor/EditableImage";
import {
  DEFAULT_SEGMENT_VISUAL_STYLE,
  getSegmentVisualStyle,
  updateSegmentVisualStyle,
} from "../segmentVisualStyle";
import { effectiveSectionPalette, getElementSectionContext, sectionVisualPath } from "../elementEditing";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";

const LEVELS = [
  { value: 1, label: "Compacto" },
  { value: 2, label: "Normal" },
  { value: 3, label: "Amplio" },
];

function ShellFieldControl({ field, form, disabled, onCommit, onUploadImage, onRemoveImage, fallbackOverride = "" }) {
  if (!field) return null;
  const rawValue = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");

  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return (
      <FormControlLabel
        control={<Switch checked={rawValue !== false} disabled={disabled} onChange={(event) => onCommit(field.path, event.target.checked)} />}
        label={field.label}
      />
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.IMAGE) {
    return (
      <EditableImage
        value={rawValue || ""}
        label={field.label || "Imagen"}
        height={140}
        disabled={disabled}
        onUpload={(file, metadata) => onUploadImage?.(field.path, file, metadata)}
        onRemove={() => onRemoveImage?.(field.path)}
      />
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label={field.label}
        value={rawValue ?? field.defaultValue ?? ""}
        disabled={disabled}
        inputProps={{ min: field.min, max: field.max, step: field.step }}
        onChange={(event) => {
          const next = event.target.value === "" ? "" : Number(event.target.value);
          onCommit(field.path, next);
        }}
      />
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    const ownValue = String(rawValue || "").trim();
    const fallbackValue = String(fallbackOverride || (field.fallbackPath
      ? getValueAtDescriptorPath(form, field.fallbackPath, field.fallbackValue || "#000000")
      : (field.fallbackValue || field.overrideSeedValue || "#000000")) || "#000000");
    const effectiveValue = ownValue || fallbackValue;
    const pickerValue = /^#[0-9a-f]{6}$/i.test(effectiveValue) ? effectiveValue : "#000000";
    const inherited = !ownValue;
    return (
      <Stack spacing={0.75} sx={{ p: 1.1, border: "1px solid rgba(25,36,47,.1)", borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 900 }}>{field.label}</Typography>
          <Chip size="small" label={inherited ? "Hereda del sitio" : "Personalizado"} variant={inherited ? "outlined" : "filled"} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            component="input"
            type="color"
            aria-label={field.label}
            value={pickerValue}
            disabled={disabled}
            onChange={(event) => onCommit(field.path, event.target.value)}
            sx={{ width: 46, height: 38, p: 0.2, border: "1px solid rgba(25,36,47,.14)", borderRadius: 1.5, bgcolor: "transparent", cursor: disabled ? "default" : "pointer" }}
          />
          <Typography variant="caption" sx={{ flex: 1, color: "rgba(25,36,47,.64)", fontFamily: "monospace" }}>{effectiveValue || "—"}</Typography>
          {!inherited ? (
            <Button size="small" disabled={disabled} onClick={() => onCommit(field.path, "")} sx={{ textTransform: "none", fontWeight: 850 }}>
              Heredar
            </Button>
          ) : null}
        </Stack>
      </Stack>
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select label={field.label} value={rawValue ?? ""} onChange={(event) => onCommit(field.path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA}
      minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined}
      label={field.label}
      value={rawValue ?? ""}
      disabled={disabled}
      inputProps={{ maxLength: field.maxLength }}
      onChange={(event) => onCommit(field.path, event.target.value)}
    />
  );
}

export default function SectionShellEditor({ sectionId, form, disabled, onCommit, onUploadImage, onRemoveImage, onOpenGlobalPalette }) {
  const context = React.useMemo(() => getElementSectionContext(form, sectionId), [form, sectionId]);
  const visualPath = sectionVisualPath(context);
  const currentVisual = context?.layout?.segment?.visualStyle || {};
  const visual = context.sectionType === "custom"
    ? { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...currentVisual }
    : getSegmentVisualStyle(form?.theme, context.sectionType);
  const shellFields = (context?.definition?.shellPropertyPaths || [])
    .map((path) => (context?.definition?.properties || []).find((property) => property?.path === path))
    .filter(Boolean);
  const shellColorFields = shellFields.filter((field) => field.type === SEGMENT_FIELD_TYPES.COLOR);
  const aboutLayoutField = context?.sectionType === "about"
    ? shellFields.find((field) => field.path === "about.layout") || null
    : null;
  const customLayoutField = context?.sectionType === "custom"
    ? shellFields.find((field) => /\.layout$/.test(String(field?.path || ""))) || null
    : null;
  const footerStyleField = context?.sectionType === "footer"
    ? shellFields.find((field) => field.path === "footer.style") || null
    : null;
  const shellBehaviorFields = shellFields.filter((field) =>
    field.type !== SEGMENT_FIELD_TYPES.COLOR && field !== aboutLayoutField && field !== customLayoutField && field !== footerStyleField
  );
  const shellAdvancedGroups = (context?.definition?.shellAdvancedGroups || [])
    .map((group) => ({
      ...group,
      fields: (group?.paths || [])
        .map((path) => (context?.definition?.properties || []).find((property) => property?.path === path))
        .filter(Boolean),
    }))
    .filter((group) => group.fields.length);
  const showVisualBackground = context?.definition?.shellVisualBackground !== false;
  const showVisualPalette = context?.definition?.shellVisualPalette !== false;
  const effectivePalette = effectiveSectionPalette(form, context);
  const paletteTokens = [
    ["background", "Fondo"],
    ["surface", "Superficie"],
    ["primary", "Acento"],
    ["secondary", "Títulos"],
    ["mutedText", "Texto"],
  ];
  const shellColorFallback = (field) => {
    const path = String(field?.path || "");
    if (!path.startsWith(`appearance.${context?.sectionType}.`)) return "";
    const role = path.split(".").pop();
    if (role === "backgroundColor") return effectivePalette.background || "";
    if (role === "surfaceColor") return effectivePalette.surface || "";
    if (role === "accentColor") return effectivePalette.primary || "";
    if (role === "titleColor") return effectivePalette.secondary || effectivePalette.text || "";
    if (role === "textColor") return effectivePalette.mutedText || effectivePalette.text || "";
    return "";
  };

  const commitVisual = (patch) => {
    if (context.sectionType === "custom" && visualPath) {
      onCommit(visualPath, { ...currentVisual, ...patch });
      return;
    }
    const nextTemplate = updateSegmentVisualStyle(form?.theme?.template, context.sectionType, patch);
    onCommit("theme.template", nextTemplate);
  };

  const commitShellVisual = (patch) => {
    if (!visualPath) return;
    onCommit(visualPath, { ...currentVisual, ...patch });
  };

  return (
    <Stack spacing={1.5}>
      {aboutLayoutField ? (
        <Stack spacing={0.8}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Distribución</Typography>
          <Stack direction="row" spacing={1}>
            {(aboutLayoutField.options || []).map((option) => {
              const selected = getValueAtDescriptorPath(form, aboutLayoutField.path, "image-left") === option.value;
              const imageRight = option.value === "image-right";
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant={selected ? "contained" : "outlined"}
                  disabled={disabled}
                  aria-pressed={selected}
                  onClick={() => onCommit(aboutLayoutField.path, option.value)}
                  sx={{
                    flex: 1,
                    minHeight: 88,
                    p: 1,
                    display: "grid",
                    gridTemplateColumns: imageRight ? "1.15fr .85fr" : ".85fr 1.15fr",
                    gap: 0.6,
                    alignItems: "stretch",
                    textTransform: "none",
                    borderRadius: 2.5,
                  }}
                >
                  <Box sx={{ order: imageRight ? 2 : 1, borderRadius: 1.5, bgcolor: selected ? "rgba(255,255,255,.34)" : "rgba(25,36,47,.10)" }} />
                  <Stack sx={{ order: imageRight ? 1 : 2, py: 0.6 }} spacing={0.45}>
                    <Box sx={{ height: 7, borderRadius: 99, bgcolor: selected ? "rgba(255,255,255,.72)" : "rgba(25,36,47,.24)" }} />
                    <Box sx={{ height: 7, width: "82%", borderRadius: 99, bgcolor: selected ? "rgba(255,255,255,.5)" : "rgba(25,36,47,.16)" }} />
                    <Typography variant="caption" sx={{ mt: "auto !important", fontWeight: 900 }}>{option.label}</Typography>
                  </Stack>
                </Button>
              );
            })}
          </Stack>
        </Stack>
      ) : null}

      {customLayoutField ? (
        <Stack spacing={0.8}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Distribución</Typography>
          <Stack direction="row" spacing={0.8}>
            {(customLayoutField.options || []).map((option) => {
              const selected = getValueAtDescriptorPath(form, customLayoutField.path, "image-right") === option.value;
              const centered = option.value === "centered";
              const imageLeft = option.value === "image-left";
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant={selected ? "contained" : "outlined"}
                  disabled={disabled}
                  aria-pressed={selected}
                  aria-label={`Usar ${option.label}`}
                  onClick={() => onCommit(customLayoutField.path, option.value)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 96,
                    p: 0.8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.65,
                    alignItems: "stretch",
                    textTransform: "none",
                    borderRadius: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 52,
                      p: 0.6,
                      display: "grid",
                      gridTemplateColumns: centered ? "1fr" : "1fr 1fr",
                      gap: 0.45,
                      alignItems: "stretch",
                      borderRadius: 1.5,
                      bgcolor: selected ? "rgba(255,255,255,.18)" : "rgba(25,36,47,.05)",
                      border: "1px solid rgba(25,36,47,.12)",
                    }}
                  >
                    {!centered ? (
                      <Box sx={{ order: imageLeft ? 1 : 2, borderRadius: 1, bgcolor: selected ? "rgba(255,255,255,.64)" : "rgba(25,36,47,.18)" }} />
                    ) : null}
                    <Stack sx={{ order: imageLeft ? 2 : 1, justifyContent: "center", alignItems: centered ? "center" : "stretch" }} spacing={0.4}>
                      <Box sx={{ width: centered ? "72%" : "100%", height: 6, borderRadius: 99, bgcolor: selected ? "rgba(255,255,255,.78)" : "rgba(25,36,47,.28)" }} />
                      <Box sx={{ width: centered ? "56%" : "76%", height: 6, borderRadius: 99, bgcolor: selected ? "rgba(255,255,255,.5)" : "rgba(25,36,47,.16)" }} />
                    </Stack>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 900 }}>
                    {centered ? "Centrada" : imageLeft ? "Imagen izquierda" : "Imagen derecha"}
                  </Typography>
                </Button>
              );
            })}
          </Stack>
        </Stack>
      ) : null}

      {footerStyleField ? (
        <Stack spacing={0.8}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Estilo del pie de página</Typography>
          <Stack direction="row" spacing={0.8}>
            {(footerStyleField.options || []).map((option, index) => {
              const selected = getValueAtDescriptorPath(form, footerStyleField.path, "style-1") === option.value;
              const centered = option.value === "style-3";
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant={selected ? "contained" : "outlined"}
                  disabled={disabled}
                  aria-pressed={selected}
                  aria-label={`Usar ${option.label}`}
                  onClick={() => onCommit(footerStyleField.path, option.value)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 96,
                    p: 0.8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.65,
                    alignItems: "stretch",
                    textTransform: "none",
                    borderRadius: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 52,
                      p: 0.6,
                      display: "flex",
                      flexDirection: centered ? "column" : "row",
                      alignItems: centered ? "center" : "flex-start",
                      justifyContent: centered ? "center" : "space-between",
                      gap: 0.45,
                      borderRadius: 1.5,
                      bgcolor: option.value === "style-2" ? "rgba(255,255,255,.72)" : "rgba(25,36,47,.16)",
                      border: "1px solid rgba(25,36,47,.12)",
                    }}
                  >
                    {[0, 1, 2].map((item) => (
                      <Box
                        key={`${option.value}:${item}`}
                        sx={{
                          width: centered ? `${72 - item * 12}%` : index === 0 && item === 0 ? "42%" : "24%",
                          height: centered ? 6 : 28 - item * 3,
                          borderRadius: 1,
                          bgcolor: selected ? "rgba(255,255,255,.72)" : "rgba(25,36,47,.22)",
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 900 }}>{option.label}</Typography>
                </Button>
              );
            })}
          </Stack>
        </Stack>
      ) : null}

      {shellBehaviorFields.length ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Comportamiento general</Typography>
          {shellBehaviorFields.map((field) => (
            <ShellFieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ))}
        </>
      ) : null}

      {shellAdvancedGroups.map((group) => (
        <Accordion
          key={group.label || group.fields.map((field) => field.path).join(":")}
          disableGutters
          elevation={0}
          sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&::before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 48 }}>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <Typography variant="body2" sx={{ fontWeight: 950 }}>{group.label || "Opciones avanzadas"}</Typography>
              <Chip size="small" label="Avanzado" variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1}>
              {group.fields.map((field) => (
                <ShellFieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      {shellColorFields.length ? (
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Paleta del bloque</Typography>
          {shellColorFields.map((field) => (
            <ShellFieldControl
              key={field.path}
              field={field}
              form={form}
              disabled={disabled}
              onCommit={onCommit}
              fallbackOverride={shellColorFallback(field)}
            />
          ))}
        </Stack>
      ) : null}

      {context?.sectionType === "footer" ? (
        <Box sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5, bgcolor: "rgba(25,36,47,.025)" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 950 }}>Paleta global</Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>El footer usa los colores generales del sitio.</Typography>
            </Box>
            <Button size="small" variant="outlined" disabled={disabled || !onOpenGlobalPalette} onClick={onOpenGlobalPalette} sx={{ flexShrink: 0, textTransform: "none", fontWeight: 900 }}>
              Editar paleta global
            </Button>
          </Stack>
        </Box>
      ) : null}

      {showVisualBackground ? <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Fondo de la sección</Typography> : null}
      {showVisualBackground && visualPath ? (
        <EditableImage
          value={currentVisual.backgroundImageUrl || ""}
          label="Imagen de fondo"
          disabled={disabled}
          onUpload={(file, metadata) => onUploadImage?.(`${visualPath}.backgroundImageUrl`, file, metadata)}
          onRemove={() => onRemoveImage?.(`${visualPath}.backgroundImageUrl`)}
        />
      ) : null}
      {showVisualBackground && visualPath ? (
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel>Posición del fondo</InputLabel>
          <Select
            label="Posición del fondo"
            value={currentVisual.backgroundImagePosition || "center"}
            onChange={(event) => commitShellVisual({ backgroundImagePosition: event.target.value })}
          >
            {[
              ["center", "Centro"], ["top", "Arriba"], ["bottom", "Abajo"], ["left", "Izquierda"], ["right", "Derecha"],
            ].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </Select>
        </FormControl>
      ) : null}

      <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Composición</Typography>
      {[
        ["spacing", "Espaciado"],
        ["titleSize", "Escala de títulos"],
        ["imageSize", "Escala de imágenes"],
      ].map(([key, label]) => (
        <FormControl key={key} fullWidth size="small" disabled={disabled}>
          <InputLabel>{label}</InputLabel>
          <Select label={label} value={visual[key] || 2} onChange={(event) => commitVisual({ [key]: Number(event.target.value) })}>
            {LEVELS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
        </FormControl>
      ))}

      {showVisualPalette && visualPath ? (
        <FormControlLabel
          control={<Switch checked={currentVisual.useGlobalColors !== false} disabled={disabled} onChange={(event) => commitShellVisual({ useGlobalColors: event.target.checked })} />}
          label="Usar paleta global como base"
        />
      ) : null}
      {showVisualPalette && visualPath && currentVisual.useGlobalColors === false ? (
        <Stack spacing={0.8}>
          <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Paleta de esta sección</Typography>
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            {paletteTokens.map(([key, label]) => (
              <Stack key={key} spacing={0.35} alignItems="center" sx={{ minWidth: 58 }}>
                <Box
                  component="input"
                  type="color"
                  aria-label={`${label} de la sección`}
                  value={String(effectivePalette[key] || "#000000").match(/^#[0-9a-f]{6}$/i) ? effectivePalette[key] : "#000000"}
                  disabled={disabled}
                  onChange={(event) => commitShellVisual({
                    useGlobalColors: false,
                    colors: { ...(currentVisual.colors || {}), [key]: event.target.value },
                  })}
                  sx={{ width: 42, height: 36, p: 0.2, border: "1px solid rgba(25,36,47,.14)", borderRadius: 1.5, bgcolor: "transparent", cursor: disabled ? "default" : "pointer" }}
                />
                <Typography variant="caption" sx={{ fontSize: ".68rem", color: "rgba(25,36,47,.64)" }}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
        Los textos, botones e imágenes de contenido se editan seleccionándolos directamente en la vista previa.
      </Typography>
    </Stack>
  );
}
